<template>
  <div class="multi-select" @click.stop>
    <div
      class="multi-select__trigger"
      :class="{ 'multi-select__trigger--active': isOpen }"
      @click="toggle"
    >
      <div class="multi-select__tags">
        <span
          v-for="tag in selectedTags"
          :key="tag.value"
          class="multi-select__tag"
          @click.stop="removeTag(tag.value)"
        >
          {{ tag.label }}
          <span class="multi-select__tag-remove">×</span>
        </span>
        <span
          v-if="selectedTags.length === 0"
          class="multi-select__placeholder"
        >
          {{ props.placeholder }}
        </span>
      </div>
      <span class="multi-select__arrow">▼</span>
    </div>

    <div v-if="isOpen" class="multi-select__dropdown" @click.stop>
      <div v-if="enableSearch" class="multi-select__search">
        <input
          ref="searchRef"
          v-model="searchText"
          type="text"
          class="multi-select__search-input"
          placeholder="搜索..."
          @input="onSearch"
        />
      </div>
      <div class="multi-select__options">
        <div
          v-for="option in filteredOptions"
          :key="option[props.valueKey]"
          class="multi-select__option"
          :class="{
            'multi-select__option--selected': isSelected(option[props.valueKey])
          }"
          @click="toggleOption(option[props.valueKey])"
        >
          <div class="multi-select__option-content">
            <span class="multi-select__option-label">{{
              option[props.labelKey]
            }}</span>
          </div>
          <span
            v-if="isSelected(option[props.valueKey])"
            class="multi-select__option-check"
            >✓</span
          >
        </div>
        <div
          v-if="filteredOptions.length === 0"
          class="multi-select__no-options"
        >
          暂无选项
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue'

export type MultiSelectValue = string | number

interface MultiSelectOption {
  [key: string]: any
  label?: string
  value?: MultiSelectValue
}

interface Props {
  modelValue: MultiSelectValue[]
  options: MultiSelectOption[]
  labelKey?: string
  valueKey?: string
  enableSearch?: boolean
  disabled?: boolean
  placeholder?: string
}

interface Emits {
  (e: 'update:modelValue', value: MultiSelectValue[]): void
  (e: 'change', value: MultiSelectValue[]): void
}

const props = withDefaults(defineProps<Props>(), {
  labelKey: 'label',
  valueKey: 'value',
  enableSearch: false,
  disabled: false,
  placeholder: '请选择'
})

const emit = defineEmits<Emits>()

const isOpen = ref(false)
const searchText = ref('')
const searchRef = ref<HTMLInputElement>()

const isSelected = (value: MultiSelectValue): boolean => {
  return props.modelValue.includes(value)
}

const selectedTags = computed(() => {
  return props.options
    .filter(option => props.modelValue.includes(option[props.valueKey!]))
    .map(option => ({
      label: option[props.labelKey],
      value: option[props.valueKey]
    }))
})

const filteredOptions = computed(() => {
  if (!props.enableSearch || !searchText.value) {
    return props.options
  }
  const searchLower = searchText.value.toLowerCase()
  return props.options.filter(option => {
    const label = String(option[props.labelKey] || '').toLowerCase()
    return label.includes(searchLower)
  })
})

const toggle = () => {
  if (props.disabled) return
  isOpen.value = !isOpen.value
  if (isOpen.value && props.enableSearch && searchRef.value) {
    nextTick(() => {
      searchRef.value?.focus()
    })
  }
}

const toggleOption = (value: MultiSelectValue) => {
  if (props.disabled) return

  const index = props.modelValue.indexOf(value)
  let newValue: MultiSelectValue[]

  if (index > -1) {
    newValue = props.modelValue.filter(v => v !== value)
  } else {
    newValue = [...props.modelValue, value]
  }

  emit('update:modelValue', newValue)
  emit('change', newValue)
}

const removeTag = (value: MultiSelectValue) => {
  if (props.disabled) return

  const newValue = props.modelValue.filter(v => v !== value)
  emit('update:modelValue', newValue)
  emit('change', newValue)
}

const onSearch = () => {
  // 搜索逻辑已在 computed 中处理
}

// 点击外部关闭下拉框
document.addEventListener('click', event => {
  if (isOpen.value && event.target instanceof Element) {
    const target = event.target as Element
    const multiSelect = target.closest('.multi-select')
    if (!multiSelect) {
      isOpen.value = false
      searchText.value = ''
    }
  }
})
</script>

<style scoped>
.multi-select {
  position: relative;
  min-height: 36px;
}

.multi-select__trigger {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 32px 6px 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
}

.multi-select__trigger:hover {
  border-color: #d1d5db;
}

.multi-select__trigger--active {
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
}

.multi-select__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  flex: 1;
}

.multi-select__tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background-color: #eff6ff;
  color: #3b82f6;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.multi-select__tag-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: #dbeafe;
  color: #3b82f6;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  transition: all 0.2s ease;
}

.multi-select__tag-remove:hover {
  background-color: #bfdbfe;
}

.multi-select__placeholder {
  color: #9ca3af;
  font-size: 13px;
}

.multi-select__arrow {
  color: #6b7280;
  font-size: 10px;
  transition: transform 0.2s ease;
}

.multi-select__trigger--active .multi-select__arrow {
  transform: rotate(180deg);
}

.multi-select__dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  max-height: 240px;
  overflow-y: auto;
  z-index: 1000;
}

.multi-select__search {
  padding: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.multi-select__search-input {
  width: 100%;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  outline: none;
}

.multi-select__search-input:focus {
  border-color: #3b82f6;
}

.multi-select__options {
  padding: 4px;
}

.multi-select__option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.multi-select__option:hover {
  background-color: #f9fafb;
}

.multi-select__option--selected {
  background-color: #eff6ff;
}

.multi-select__option-content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.multi-select__option-label {
  font-size: 13px;
  color: #374151;
}

.multi-select__option-check {
  color: #3b82f6;
  font-size: 12px;
  font-weight: bold;
}

.multi-select__no-options {
  padding: 12px;
  text-align: center;
  font-size: 12px;
  color: #9ca3af;
}
</style>
